import { findResult, jobStatus, } from '@emdzej/ncsx-wire';
/**
 * Job NCSEXPER's `coapiReadFgNr` (NCSEXPER.EXE `0x0042e430`) actually issues. The
 * earlier candidate-list approach guessed multiple aliases; ghidra trace shows there's
 * exactly one: `FGNR_LESEN`. Other names (`FG_NR_LESEN`, `IDENT_LESEN`) don't appear
 * in NCSEXPER's identity-read path.
 */
const VIN_JOB = 'FGNR_LESEN';
/**
 * EDIABAS result-set field name the SGBD emits the VIN under. Ghidra-verified via
 * `apiResultText("FAHRGESTELL_NR", …)` inside `coapiReadFgNr`. NOT one of the
 * `FG_NR_LANG`/`FG_NR`/`FGNR_LANG`/`FGNR`/`IDENT` guesses I had originally — those
 * jobs/fields don't exist on these SGBDs.
 */
const VIN_RESULT_NAME = 'FAHRGESTELL_NR';
/**
 * VINs are 17 chars of [A-HJ-NPR-Z0-9] (no I/O/Q). Don't enforce the check digit — just
 * guard against the SG returning a placeholder like `00000000000000000` (some SGs
 * report all-zeros when the FAHRGESTELL_NR field isn't paired to the car yet).
 */
function looksLikeVin(s) {
    return s.length === 17 && /^[A-HJ-NPR-Z0-9]{17}$/.test(s) && !/^0+$/.test(s);
}
/**
 * Placeholder prefix NCSEXPER's `coapiSetFgNr` (NCSEXPER.EXE `0x0042a560`)
 * uses to pad the 7-char ECU FGNR into a 17-char VIN-shaped string.
 * Positions 1-3 = "WBA" (BMW AG WMI), positions 4-10 = "AA00000" (filler
 * — NOT derived from FA/chassis; NCSEXPER doesn't reconstruct the real
 * mid-VIN). The 7-char FGNR substitutes positions 11-17.
 */
const NCSEXPER_VIN_PLACEHOLDER = 'WBAAA00000';
export function padFgnrToVin(raw) {
    const s = String(raw ?? '').trim().toUpperCase();
    if (s.length === 7) {
        return {
            vin: NCSEXPER_VIN_PLACEHOLDER + s,
            fgnr: s,
            padded: true,
        };
    }
    if (s.length === 17) {
        return { vin: s, fgnr: s.slice(10), padded: false };
    }
    if (s.length === 18) {
        const trimmed = s.slice(0, 17);
        return { vin: trimmed, fgnr: trimmed.slice(10), padded: false };
    }
    return { vin: s, fgnr: s.length >= 7 ? s.slice(-7) : s, padded: false };
}
export async function readVin(ediabas, sgbd) {
    let response;
    try {
        /* IEdiabas merges loadSgbd + executeJob into one call. */
        response = await ediabas.job(sgbd, VIN_JOB);
    }
    catch (err) {
        return {
            ok: false,
            error: err instanceof Error ? err.message : String(err),
        };
    }
    const status = jobStatus(response);
    if (status && status !== 'OKAY') {
        return { ok: false, jobStatus: status };
    }
    const r = findResult(response, VIN_RESULT_NAME);
    if (!r) {
        return {
            ok: false,
            jobStatus: status || 'OKAY',
            error: `${VIN_JOB} ran but no ${VIN_RESULT_NAME} field in response`,
        };
    }
    const raw = typeof r.value === 'string' ? r.value : String(r.value);
    const trimmed = raw.trim().toUpperCase();
    if (!looksLikeVin(trimmed)) {
        return {
            ok: false,
            jobStatus: status || 'OKAY',
            error: `FAHRGESTELL_NR returned "${trimmed}" (not a valid VIN)`,
        };
    }
    return { ok: true, vin: trimmed, jobStatus: status || 'OKAY' };
}
//# sourceMappingURL=vin.js.map